import type { CreateSummaryTemplateInput } from '@/types/composition-functions';
import type { PdfHeaderConfig } from '@/types/wizardCommon';

export type { PdfHeaderConfig };

/**
 * Pure logic for the conversational summary-template wizard.
 * Follows resultTemplateWizard.ts / broadsheetWizard.ts: payload
 * composition stays free of Vue/Apollo so it can be unit tested.
 */

export type SummaryCalculationMethod = 'cgpa' | 'weighted';
export type SummaryHeaderStyle = 'grouped' | 'flat';
export type SummaryScope = 'previous' | 'current' | 'cumulative';
export type SummaryOutcomeKey = 'outstanding_count' | 'failed_courses' | 'degree_class' | 'status';
export type SummaryExtraKey = 'grade_summary' | 'failed_courses_list' | 'legend';

export type ProgrammePreset = 'undergraduate' | 'pgd' | 'postgraduate_a' | 'postgraduate_b' | 'custom';
export type ReportMode = 'semester' | 'final_result';
export type DegreeClassVisibility = 'auto' | 'always' | 'never';

export interface ClassificationCategory {
  label: string;
  key: string;
  min_threshold: number | null;
  status?: string | null;
}

export interface GraduatingStatusLabels {
  passing: string;
  failingPrefix: string;
  proceedTo: string | null;
}

export interface LegendConfig {
  showClassPerformance: boolean;
  showInterpretation: boolean;
  interpretationLines: string[];
}

export interface GraduationConditions {
  checkOutstandingCourses: boolean;
  checkMinUnits: boolean;
  checkFinalYear: boolean;
}

export interface SummaryWizardAnswers {
  name: string;
  calculationMethod: SummaryCalculationMethod;
  headerStyle: SummaryHeaderStyle;
  scopes: SummaryScope[];
  /** true → TCP/TNUP/TNUR + average per block; false → just the GPA/CGPA column. */
  fullStats: boolean;
  outcomes: SummaryOutcomeKey[];
  extras: SummaryExtraKey[];
  includeCampus: boolean;
  showSignatures: boolean;
  signatureTitles: string[];
  graduationConditions: GraduationConditions;
  pdfHeader: PdfHeaderConfig;
  programmePreset: ProgrammePreset;
  reportMode: ReportMode;
  degreeClassVisibility: DegreeClassVisibility;
  graduatingCategories: ClassificationCategory[];
  nonGraduatingStatuses: ClassificationCategory[];
  graduatingStatusLabels: GraduatingStatusLabels;
  legend: LegendConfig;
}

// PdfHeaderConfig imported from @/types/wizardCommon

export interface SummaryColumnDraft {
  label: string;
  key: string;
  type: string;
  group: string | null;
  calculation_function: string | null;
  calculation_arguments: { scope: SummaryScope } | null;
}

export const SCOPE_ORDER: SummaryScope[] = ['previous', 'current', 'cumulative'];

export const SCOPE_DETAILS: Record<SummaryScope, { label: string; group: string; prefix: string }> = {
  previous: { label: 'Previous semesters', group: 'Previous', prefix: 'prev' },
  current: { label: 'Current semester', group: 'Current', prefix: 'curr' },
  cumulative: { label: 'Cumulative (all semesters)', group: 'Cumulative', prefix: 'cum' },
};

function getScopeLabels(scope: SummaryScope, method: SummaryCalculationMethod): { pointsLabel: string; avgLabel: string; flatStats: Record<string, string>; flatAvg: string } {
  const isWeighted = method === 'weighted';
  const p = SCOPE_DETAILS[scope].prefix;
  const isCum = scope !== 'current';

  const pointsLabel = isWeighted ? 'TW' : 'TCP';
  const avgLabel = isWeighted
    ? (isCum ? 'CWA' : 'WA')
    : (isCum ? 'CGPA' : 'GPA');

  const flatStats: Record<string, string> = {
    [pointsLabel]: scope === 'cumulative'
      ? (isWeighted ? 'CTW' : 'CTCP')
      : `${scope === 'previous' ? 'Prev' : 'Curr'} ${isCum ? (isWeighted ? 'CTW' : 'CTCP') : pointsLabel}`,
    TNUP: scope === 'cumulative' ? 'CTNUP' : `${scope === 'previous' ? 'Prev' : 'Curr'} ${isCum ? 'CTNUP' : 'TNUP'}`,
    TNUR: scope === 'cumulative' ? 'CTNUR' : `${scope === 'previous' ? 'Prev' : 'Curr'} ${isCum ? 'CTNUR' : 'TNUR'}`,
  };

  const flatAvg = scope === 'cumulative'
    ? avgLabel
    : `${scope === 'previous' ? 'Prev' : 'Curr'} ${avgLabel}`;

  return { pointsLabel, avgLabel, flatStats, flatAvg };
}

// Legacy compat — used by existing code that doesn't pass method
export const FLAT_LABEL_CONFIG: Record<SummaryScope, { stats: Record<string, string>; average: string }> = {
  previous: { stats: { TCP: 'Prev CTCP', TNUP: 'Prev CTNUP', TNUR: 'Prev CTNUR' }, average: 'Prev CGPA' },
  current: { stats: { TCP: 'Curr TCP', TNUP: 'Curr TNUP', TNUR: 'Curr TNUR' }, average: 'Curr GPA' },
  cumulative: { stats: { TCP: 'CTCP', TNUP: 'CTNUP', TNUR: 'CTNUR' }, average: 'CGPA' },
};

export const OUTCOME_OPTIONS: Array<{ key: SummaryOutcomeKey; label: string; calculation_function: string; columnKey: string; columnLabel: string; position?: 'after_current' }> = [
  { key: 'outstanding_count', label: 'Outstanding Count', calculation_function: 'OUTSTANDING_COUNT', columnKey: 'outstanding_count', columnLabel: 'Outstd', position: 'after_current' },
  { key: 'failed_courses', label: 'Outstanding Courses', calculation_function: 'FAILED_COURSES', columnKey: 'failed_courses', columnLabel: 'Outstanding Course' },
  { key: 'degree_class', label: 'Degree Class', calculation_function: 'DEGREE_CLASS', columnKey: 'degree_class', columnLabel: 'Degree Class' },
  { key: 'status', label: 'Academic Status', calculation_function: 'STATUS', columnKey: 'status', columnLabel: 'Status' },
];

export const EXTRA_OPTIONS: Array<{ key: SummaryExtraKey; label: string; description: string }> = [
  { key: 'grade_summary', label: 'Grade distribution', description: 'How many A\'s, B\'s, C\'s… below the table' },
  { key: 'failed_courses_list', label: 'Failed courses list', description: 'Each student’s failed courses listed below' },
  { key: 'legend', label: 'Legend', description: 'Explains abbreviations like TCP and TNUP' },
];

export const PRESET_CONFIGS: Record<Exclude<ProgrammePreset, 'custom'>, {
  label: string;
  description: string;
  calculationMethod: SummaryCalculationMethod;
  graduatingCategories: ClassificationCategory[];
  nonGraduatingStatuses: ClassificationCategory[];
  graduatingStatusLabels: GraduatingStatusLabels;
}> = {
  undergraduate: {
    label: 'Undergraduate',
    description: 'BSc, BA, B.Ed, B.Eng — uses CGPA (5.0 scale)',
    calculationMethod: 'cgpa',
    graduatingCategories: [
      { label: 'First Class', key: 'first_class', min_threshold: 4.50 },
      { label: 'Second Class Upper', key: 'second_class_upper', min_threshold: 3.50 },
      { label: 'Second Class Lower', key: 'second_class_lower', min_threshold: 2.40 },
      { label: 'Third Class', key: 'third_class', min_threshold: 1.50 },
      { label: 'Pass', key: 'pass', min_threshold: 1.00 },
      { label: 'Fail', key: 'fail', min_threshold: null },
    ],
    nonGraduatingStatuses: [
      { label: 'GSD (Excellent)', key: 'excellent', min_threshold: 4.50 },
      { label: 'GSD (V. Good)', key: 'very_good', min_threshold: 3.50 },
      { label: 'GSD (Good)', key: 'good', min_threshold: 2.40 },
      { label: 'GSD (Average)', key: 'average', min_threshold: 1.50 },
      { label: 'PRB (Fair)', key: 'fair', min_threshold: 1.00 },
      { label: 'WRN (V. Poor)', key: 'fail', min_threshold: null },
    ],
    graduatingStatusLabels: { passing: 'Graduating', failingPrefix: 'Not Graduating', proceedTo: null },
  },
  pgd: {
    label: 'PGD / MBA',
    description: 'Postgraduate Diploma, MBA — uses CGPA (5.0 scale)',
    calculationMethod: 'cgpa',
    graduatingCategories: [
      { label: 'Distinction', key: 'distinction', min_threshold: 4.50 },
      { label: 'Credit', key: 'credit', min_threshold: 3.50 },
      { label: 'Pass', key: 'pass', min_threshold: 3.00 },
      { label: 'Fail', key: 'fail', min_threshold: null },
    ],
    nonGraduatingStatuses: [
      { label: 'GSD (Excellent)', key: 'excellent', min_threshold: 4.50 },
      { label: 'GSD (V. Good)', key: 'very_good', min_threshold: 3.50 },
      { label: 'GSD (Good)', key: 'good', min_threshold: 2.40 },
      { label: 'GSD (Average)', key: 'average', min_threshold: 1.50 },
      { label: 'PRB (Fair)', key: 'fair', min_threshold: 1.00 },
      { label: 'WRN (V. Poor)', key: 'fail', min_threshold: null },
    ],
    graduatingStatusLabels: { passing: 'Graduating', failingPrefix: 'Incomplete Result', proceedTo: null },
  },
  postgraduate_a: {
    label: 'Postgraduate (Variant A)',
    description: 'MSc, MA, PhD — Weighted Average with Distinction/Credit/Merit thresholds (60/55/50)',
    calculationMethod: 'weighted',
    graduatingCategories: [
      { label: 'Distinction', key: 'distinction', min_threshold: 60, status: 'Proceed to PhD' },
      { label: 'Credit', key: 'credit', min_threshold: 55, status: 'Proceed to MPhil/PhD' },
      { label: 'Merit', key: 'merit', min_threshold: 50, status: 'Proceed to MPhil' },
      { label: 'Pass', key: 'pass', min_threshold: 0.01, status: 'Masters Terminal' },
      { label: 'Fail', key: 'fail', min_threshold: null, status: null },
    ],
    nonGraduatingStatuses: [
      { label: 'Incomplete Result', key: 'incomplete', min_threshold: null },
    ],
    graduatingStatusLabels: { passing: 'Graduating', failingPrefix: 'Not Graduating', proceedTo: null },
  },
  postgraduate_b: {
    label: 'Postgraduate (Variant B)',
    description: 'MSc, MA, PhD — Weighted Average with Distinction/Credit/Pass thresholds (70/60/50)',
    calculationMethod: 'weighted',
    graduatingCategories: [
      { label: 'Distinction', key: 'distinction', min_threshold: 70, status: 'Proceed to PhD' },
      { label: 'Credit', key: 'credit', min_threshold: 60, status: 'Proceed to MPhil/PhD' },
      { label: 'Pass', key: 'pass', min_threshold: 50, status: 'Proceed to MPhil' },
      { label: 'Fail', key: 'fail', min_threshold: null, status: null },
    ],
    nonGraduatingStatuses: [
      { label: 'Incomplete Result', key: 'incomplete', min_threshold: null },
    ],
    graduatingStatusLabels: { passing: 'Graduating', failingPrefix: 'Not Graduating', proceedTo: null },
  },
};

const STUDENT_INFO_COLUMNS: SummaryColumnDraft[] = [
  { label: 'SN', key: 'sn', type: 'STUDENT_INFO', group: null, calculation_function: null, calculation_arguments: null },
  { label: 'Matric No', key: 'matric_no', type: 'STUDENT_INFO', group: null, calculation_function: null, calculation_arguments: null },
  { label: 'Names', key: 'names', type: 'STUDENT_INFO', group: null, calculation_function: null, calculation_arguments: null },
];

export function buildSummaryColumnDrafts(answers: SummaryWizardAnswers): SummaryColumnDraft[] {
  const drafts: SummaryColumnDraft[] = STUDENT_INFO_COLUMNS.map((c) => ({ ...c }));

  if (answers.includeCampus) {
    drafts.push({ label: 'Campus', key: 'campus', type: 'STUDENT_INFO', group: null, calculation_function: null, calculation_arguments: null });
  }

  const isFlat = answers.headerStyle === 'flat';
  const method = answers.calculationMethod;

  SCOPE_ORDER.filter((scope) => answers.scopes.includes(scope)).forEach((scope) => {
    const detail = SCOPE_DETAILS[scope];
    const sl = getScopeLabels(scope, method);
    const statFns = ['TNUR', 'TNUP', sl.pointsLabel];
    if (answers.fullStats) {
      statFns.forEach((fn) => {
        const flatLabel = fn === 'TNUR' ? sl.flatStats.TNUR : fn === 'TNUP' ? sl.flatStats.TNUP : sl.flatStats[sl.pointsLabel];
        drafts.push({
          label: isFlat ? flatLabel : fn,
          key: `${detail.prefix}_${fn.toLowerCase()}`,
          type: 'CALCULATED',
          group: isFlat ? null : detail.group,
          calculation_function: fn,
          calculation_arguments: { scope },
        });
      });
    }
    drafts.push({
      label: isFlat ? sl.flatAvg : sl.avgLabel,
      key: `${detail.prefix}_${sl.avgLabel.toLowerCase()}`,
      type: 'CALCULATED',
      group: isFlat ? null : detail.group,
      calculation_function: sl.avgLabel,
      calculation_arguments: { scope },
    });

    // Insert outstanding count after current scope (between Current GPA and Cumulative)
    if (scope === 'current') {
      OUTCOME_OPTIONS.filter((o) => o.position === 'after_current' && answers.outcomes.includes(o.key)).forEach((outcome) => {
        drafts.push({
          label: outcome.columnLabel,
          key: outcome.columnKey,
          type: 'STATUS',
          group: null,
          calculation_function: outcome.calculation_function,
          calculation_arguments: null,
        });
      });
    }
  });

  OUTCOME_OPTIONS.filter((o) => !o.position && answers.outcomes.includes(o.key)).forEach((outcome) => {
    drafts.push({
      label: outcome.columnLabel,
      key: outcome.columnKey,
      type: 'STATUS',
      group: null,
      calculation_function: outcome.calculation_function,
      calculation_arguments: null,
    });
  });

  return drafts;
}

export function buildCreateSummaryInput(answers: SummaryWizardAnswers): CreateSummaryTemplateInput {
  const drafts = buildSummaryColumnDrafts(answers);

  return {
    name: answers.name.trim(),
    description: `Created with the guided setup — ${answers.calculationMethod === 'cgpa' ? 'CGPA (grade points)' : 'weighted (score × units)'} method`,
    properties: {
      calculation_method: answers.calculationMethod,
      header_style: answers.headerStyle,
      show_grade_summary: answers.extras.includes('grade_summary'),
      show_failed_courses: answers.extras.includes('failed_courses_list'),
      show_legend: answers.extras.includes('legend'),
      include_campus: answers.includeCampus,
      show_signatures: answers.showSignatures,
      signatures: answers.showSignatures && answers.signatureTitles.length > 0
        ? answers.signatureTitles.map((title) => ({ title, show_sign: true, show_date: true }))
        : null,
      graduation_conditions: {
        check_outstanding_courses: answers.graduationConditions.checkOutstandingCourses,
        check_min_units: answers.graduationConditions.checkMinUnits,
        check_final_year: answers.graduationConditions.checkFinalYear,
      },
      programme_preset: answers.programmePreset,
      report_mode: answers.reportMode,
      degree_class_visibility: answers.degreeClassVisibility,
      graduating_categories: answers.graduatingCategories,
      non_graduating_statuses: answers.nonGraduatingStatuses.map(s => ({
        label: s.label,
        key: s.key,
        min_threshold: s.min_threshold,
      })),
      graduating_status_labels: {
        passing: answers.graduatingStatusLabels.passing,
        failing_prefix: answers.graduatingStatusLabels.failingPrefix,
        proceed_to: answers.graduatingStatusLabels.proceedTo,
      },
      pdf_header: {
        school_name: answers.pdfHeader.schoolName || null,
        report_title: answers.pdfHeader.reportTitle || null,
        semester_format: answers.pdfHeader.semesterFormat || null,
        logo_position: answers.pdfHeader.logoPosition || null,
        level_display_map: Object.keys(answers.pdfHeader.levelDisplayMap || {}).length > 0
          ? answers.pdfHeader.levelDisplayMap
          : null,
      },
    },
    columns: drafts.map((draft, index) => ({
      label: draft.label,
      key: draft.key,
      type: draft.type,
      group: draft.group,
      sequence: index + 1,
      is_visible: true,
      calculation_function: draft.calculation_function,
      calculation_arguments: draft.calculation_arguments,
    })),
  };
}

// --- Live preview ----------------------------------------------------------

export interface SummaryPreviewStudent {
  name: string;
  matric: string;
  /** Seeded academic profile per scope: [creditPoints, unitsPassed, unitsRegistered]. */
  previous: [number, number, number];
  current: [number, number, number];
}

export const SUMMARY_PREVIEW_STUDENTS: SummaryPreviewStudent[] = [
  { name: 'Adaeze Okafor', matric: 'STU/001', previous: [82, 18, 18], current: [76, 16, 16] },
  { name: 'Ibrahim Musa', matric: 'STU/002', previous: [54, 16, 18], current: [40, 12, 16] },
  { name: 'Chioma Eze', matric: 'STU/003', previous: [66, 17, 18], current: [58, 15, 16] },
];

export function degreeClassFor(average: number, method: SummaryCalculationMethod, categories?: ClassificationCategory[]): string {
  if (categories && categories.length > 0) {
    for (const cat of categories) {
      if (cat.min_threshold !== null && average >= cat.min_threshold) return cat.label;
    }
    const last = categories[categories.length - 1];
    return last?.label ?? 'Fail';
  }
  if (method === 'cgpa') {
    if (average >= 4.5) return 'First Class';
    if (average >= 3.5) return '2nd Class Upper';
    if (average >= 2.4) return '2nd Class Lower';
    if (average >= 1.5) return 'Third Class';
    if (average >= 1.0) return 'Pass';
    return 'Fail';
  }
  if (average >= 70) return 'First Class';
  if (average >= 60) return '2nd Class Upper';
  if (average >= 50) return '2nd Class Lower';
  if (average >= 45) return 'Third Class';
  if (average >= 40) return 'Pass';
  return 'Fail';
}

export interface SummaryPreviewRow {
  info: Record<string, string | number>;
  /** values keyed by column key for every generated CALCULATED/STATUS column. */
  values: Record<string, string | number>;
}

/**
 * Sample rows for the live preview with genuinely derived TCP/TNUP/TNUR,
 * GPA/CGPA and outcome values, so the sheet behaves like the real report.
 */
export function buildSummaryPreviewRows(answers: SummaryWizardAnswers): SummaryPreviewRow[] {
  // Scale grade points down to the 5-point system for the cgpa method;
  // the weighted method works on score × units magnitudes instead.
  const factor = answers.calculationMethod === 'cgpa' ? 1 : 14;

  return SUMMARY_PREVIEW_STUDENTS.map((student, index) => {
    const [prevTcp, prevTnup, prevTnur] = student.previous;
    const [currTcp, currTnup, currTnur] = student.current;
    const cumTcp = prevTcp + currTcp;
    const cumTnup = prevTnup + currTnup;
    const cumTnur = prevTnur + currTnur;

    const average = (tcp: number, tnur: number) => (tnur > 0 ? ((tcp / tnur) * (factor === 1 ? 1 : factor)).toFixed(2) : '0.00');
    const cumAverage = Number(average(cumTcp, cumTnur));

    const values: Record<string, string | number> = {
      prev_tcp: prevTcp * (factor === 1 ? 1 : factor),
      prev_tnup: prevTnup,
      prev_tnur: prevTnur,
      prev_cgpa: average(prevTcp, prevTnur),
      curr_tcp: currTcp * (factor === 1 ? 1 : factor),
      curr_tnup: currTnup,
      curr_tnur: currTnur,
      curr_gpa: average(currTcp, currTnur),
      cum_tcp: cumTcp * (factor === 1 ? 1 : factor),
      cum_tnup: cumTnup,
      cum_tnur: cumTnur,
      cum_cgpa: average(cumTcp, cumTnur),
      outstanding_count: cumTnup < cumTnur ? 1 : 0,
      failed_courses: cumTnup < cumTnur ? 'GST 103' : '—',
      degree_class: degreeClassFor(cumAverage, answers.calculationMethod, answers.graduatingCategories),
      status: (() => {
        if (cumTnup < cumTnur && answers.nonGraduatingStatuses.length > 0) {
          for (const s of answers.nonGraduatingStatuses) {
            if (s.min_threshold !== null && cumAverage >= s.min_threshold) return s.label;
          }
          const last = answers.nonGraduatingStatuses[answers.nonGraduatingStatuses.length - 1];
          return last?.label ?? 'Fail';
        }
        return cumTnup < cumTnur * 0.75 ? 'Probation' : 'Good Standing';
      })(),
    };

    return {
      info: { sn: index + 1, matric_no: student.matric, names: student.name },
      values,
    };
  });
}
